import { useCallback, useEffect, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";

import {
  formatInviteMeta,
  presentJoinAccount,
} from "../../../../packages/shared/src/join-account.ts";
import {
  acceptPendingInvite,
  fetchPendingInvites,
  type PendingInviteLoad,
} from "../../../../packages/shared/src/pending-invites.ts";

/**
 * "Join account" on the web fallback surface. Same pure model as the native app
 * (packages/shared/src/join-account.ts), so the two surfaces cannot drift on the 0 / 1 / 2+
 * decision or on the consent sentence that has to appear above the button.
 *
 * Guard Web is the non-installed path (product lock 1.3/F1). Once the officer joins, the
 * workspace shell takes over here, and they are told the native app is the day-to-day surface.
 */
export function JoinAccountPanel({
  client,
  signedInEmail,
  onJoined,
  onSignOut,
}: {
  readonly client: SupabaseClient;
  readonly signedInEmail: string | null;
  readonly onJoined: () => void;
  readonly onSignOut: () => void;
}) {
  const [load, setLoad] = useState<PendingInviteLoad | null>(null);
  const [selectedInviteId, setSelectedInviteId] = useState<string | null>(null);
  const [acceptFailure, setAcceptFailure] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoad(null);
    setAcceptFailure(null);
    setLoad(await fetchPendingInvites(client));
  }, [client]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const view = presentJoinAccount({ load, selectedInviteId, acceptFailure, signedInEmail, busy });

  const join = useCallback(async () => {
    const invite = view.selectedInvite;
    if (invite === null || busy) return;
    setBusy(true);
    setAcceptFailure(null);
    const outcome = await acceptPendingInvite(client, invite.accountId);
    if (outcome.ok) {
      setBusy(false);
      onJoined();
      return;
    }
    // A refusal changes what is still joinable, so re-read before showing the failure.
    setLoad(await fetchPendingInvites(client));
    setAcceptFailure(outcome.reason);
    setBusy(false);
  }, [busy, client, onJoined, view.selectedInvite]);

  return (
    <section className="join" aria-labelledby="join-title" aria-busy={view.kind === "loading"}>
      {view.kind === "confirm" ? <p className="join__eyebrow">{view.body}</p> : null}
      <h2 id="join-title" className="join__title">
        {view.headline}
      </h2>
      {view.kind === "confirm" ? null : <p className="join__detail">{view.body}</p>}

      {view.errorLine !== null ? (
        <div className="join__notice" role="alert">
          <p className="join__notice-title">{view.errorLine}</p>
          {view.whatToDoLine !== null ? <p className="join__notice-body">{view.whatToDoLine}</p> : null}
        </div>
      ) : null}

      {view.kind === "confirm" && view.selectedInvite !== null ? (
        <p className="join__meta">{formatInviteMeta(view.selectedInvite)}</p>
      ) : null}

      {view.kind === "choose" ? (
        <ul className="join__list" aria-label="Pending invitations">
          {view.invites.map((invite) => {
            const chosen = view.selectedInvite?.inviteId === invite.inviteId;
            return (
              <li key={invite.inviteId}>
                <button
                  className={chosen ? "join__pick join__pick--chosen" : "join__pick"}
                  type="button"
                  aria-pressed={chosen}
                  disabled={busy}
                  onClick={() => setSelectedInviteId(invite.inviteId)}
                >
                  <span className="join__pick-name">{invite.accountName}</span>
                  <span className="join__pick-meta">{formatInviteMeta(invite)}</span>
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      {(view.kind === "none" || view.kind === "unavailable") && view.errorLine === null && view.whatToDoLine !== null ? (
        <div className="join__notice">
          <p className="join__notice-title">What to do</p>
          <p className="join__notice-body">{view.whatToDoLine}</p>
        </div>
      ) : null}

      {view.consentLine !== null ? <p className="join__consent">{view.consentLine}</p> : null}

      <div className="join__actions">
        {view.primaryLabel !== null ? (
          <button className="join__submit" type="button" disabled={!view.primaryEnabled} onClick={() => void join()}>
            {busy ? "Joining…" : view.primaryLabel}
          </button>
        ) : null}
        <button
          className="join__ghost"
          type="button"
          disabled={busy || load === null}
          onClick={() => void refresh()}
        >
          {load === null ? "Checking…" : "Check again"}
        </button>
        <button className="join__ghost" type="button" onClick={onSignOut}>
          Sign out
        </button>
      </div>

      {view.signedInLine !== null ? <p className="join__hint">{view.signedInLine}</p> : null}
    </section>
  );
}
